/**
 * AxLabs Mathematics Experiments - Mathematical Modeling
 *
 * Collection of experiments exploring mathematical modeling applications.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// LINEAR MODEL FITTING
// ============================================================================
export const LinearModelFitting = createExperimentFromSchema({
    id: 'linear-model-fitting',
    name: 'Linear Model Fitting',
    domain: 'mathematics',
    description: 'Fit linear models to data and analyze residuals',
    conceptTags: ['Linear Modeling', 'Regression', 'Residuals', 'Model Fit'],
    difficulty: 4,
    stateVariables: [
        { id: 'm', name: 'Slope m', unit: 'dimensionless', initial: 1, symbol: 'm' },
        { id: 'b', name: 'Intercept b', unit: 'dimensionless', initial: 0, symbol: 'b' },
        { id: 'residualSum', name: 'RSS', unit: 'dimensionless', initial: 10, symbol: 'RSS' },
        { id: 'rSquared', name: 'R²', unit: 'dimensionless', initial: 0.95, symbol: 'R^2' },
    ],
    parameters: [
        { id: 'noiseLevel', name: 'Data Noise', unit: 'dimensionless', min: 0, max: 3, default: 0.5, symbol: '\\sigma' },
        { id: 'trueSlope', name: 'True Slope', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'm_{true}' },
    ],
    equations: [
        {
            id: 'linear-model',
            name: 'Linear Model',
            latex: 'y = mx + b',
            description: 'Simple linear relationship',
        },
        {
            id: 'residuals',
            name: 'Residuals',
            latex: 'e_i = y_i - (mx_i + b)',
            description: 'Difference between observed and predicted',
        },
    ],
    derivatives: (_state, params) => {
        const noise = getParam(params, 'noiseLevel');
        const trueSlope = getParam(params, 'trueSlope');
        // Generate synthetic data
        const n = 20;
        const data = [];
        for (let i = 0; i < n; i++) {
            const x = (i - n / 2) / 3;
            const noiseVal = (Math.sin(i * 1.3) * noise);
            data.push({ x, y: trueSlope * x + noiseVal });
        }
        // Calculate least squares fit
        const sumX = data.reduce((a, p) => a + p.x, 0);
        const sumY = data.reduce((a, p) => a + p.y, 0);
        const sumXY = data.reduce((a, p) => a + p.x * p.y, 0);
        const sumXX = data.reduce((a, p) => a + p.x * p.x, 0);
        const m = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const b = (sumY - m * sumX) / n;
        // Calculate RSS and R²
        const yMean = sumY / n;
        let ssRes = 0;
        let ssTot = 0;
        for (const p of data) {
            const predicted = m * p.x + b;
            ssRes += (p.y - predicted) ** 2;
            ssTot += (p.y - yMean) ** 2;
        }
        const rSquared = 1 - ssRes / ssTot;
        return {
            m: m - getState(_state, 'm'),
            b: b - getState(_state, 'b'),
            residualSum: ssRes - getState(_state, 'residualSum'),
            rSquared: rSquared - getState(_state, 'rSquared'),
        };
    },
    bounds: {
        m: { min: -5, max: 5 },
        b: { min: -5, max: 5 },
        rSquared: { min: 0, max: 1 },
    },
});
// ============================================================================
// EXPONENTIAL DECAY MODELING
// ============================================================================
export const ExponentialDecayModeling = createExperimentFromSchema({
    id: 'exponential-decay-modeling',
    name: 'Exponential Decay Modeling',
    domain: 'mathematics',
    description: 'Model exponential decay processes like radioactive decay',
    conceptTags: ['Exponential Decay', 'Half-Life', 'Decay Constant', 'Radioactive Dating'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time t', unit: 's', initial: 0, symbol: 't' },
        { id: 'N', name: 'Quantity N(t)', unit: 'dimensionless', initial: 100, symbol: 'N(t)' },
        { id: 'halfLife', name: 'Half-Life', unit: 's', initial: 5, symbol: 't_{1/2}' },
        { id: 'decayConst', name: 'Decay Constant λ', unit: '1/s', initial: 0.139, symbol: '\\lambda' },
        { id: 'percentage', name: 'Remaining %', unit: 'dimensionless', initial: 100, symbol: '\\%' },
    ],
    parameters: [
        { id: 'N0', name: 'Initial N₀', unit: 'dimensionless', min: 10, max: 1000, default: 100, symbol: 'N_0' },
        { id: 'halfLifeParam', name: 'Half-Life', unit: 's', min: 1, max: 20, default: 5, symbol: 't_{1/2}' },
    ],
    equations: [
        {
            id: 'decay-formula',
            name: 'Exponential Decay',
            latex: 'N(t) = N_0 e^{-\\lambda t}',
            description: 'Quantity decreases exponentially',
        },
        {
            id: 'half-life-formula',
            name: 'Half-Life Formula',
            latex: 't_{1/2} = \\frac{\\ln 2}{\\lambda}',
            description: 'Time for quantity to halve',
        },
    ],
    derivatives: (state, params) => {
        const N0 = getParam(params, 'N0');
        const halfLifeParam = getParam(params, 'halfLifeParam');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        const lambda = Math.LN2 / halfLifeParam;
        const N = N0 * Math.exp(-lambda * nextT);
        const percentage = (N / N0) * 100;
        return {
            t: 0.016,
            N: N - getState(state, 'N'),
            halfLife: halfLifeParam - getState(state, 'halfLife'),
            decayConst: lambda - getState(state, 'decayConst'),
            percentage: percentage - getState(state, 'percentage'),
        };
    },
    bounds: {
        t: { min: 0, max: 120 },
        N: { min: 0, max: 1000 },
    },
});
// ============================================================================
// LOGISTIC GROWTH CURVE
// ============================================================================
export const LogisticGrowthCurve = createExperimentFromSchema({
    id: 'logistic-growth-curve',
    name: 'Logistic Growth Curve',
    domain: 'mathematics',
    description: 'Model population growth with carrying capacity',
    conceptTags: ['Logistic Growth', 'Carrying Capacity', 'S-Curve', 'Population Dynamics'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time t', unit: 's', initial: 0, symbol: 't' },
        { id: 'P', name: 'Population P(t)', unit: 'dimensionless', initial: 10, symbol: 'P(t)' },
        { id: 'dPdt', name: 'Growth Rate dP/dt', unit: 'dimensionless/s', initial: 0.9, symbol: 'dP/dt' },
        { id: 'inflection', name: 'Inflection Point', unit: 's', initial: 2.3, symbol: 't_{inf}' },
    ],
    parameters: [
        { id: 'K', name: 'Carrying Capacity K', unit: 'dimensionless', min: 50, max: 500, default: 100, symbol: 'K' },
        { id: 'P0', name: 'Initial P₀', unit: 'dimensionless', min: 1, max: 50, default: 10, symbol: 'P_0' },
        { id: 'r', name: 'Growth Rate r', unit: '1/s', min: 0.1, max: 2, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'logistic-diff-eq',
            name: 'Logistic Differential Equation',
            latex: '\\frac{dP}{dt} = rP\\left(1 - \\frac{P}{K}\\right)',
            description: 'Growth rate decreases as population approaches carrying capacity',
        },
        {
            id: 'logistic-solution',
            name: 'Logistic Solution',
            latex: 'P(t) = \\frac{K}{1 + \\frac{K-P_0}{P_0}e^{-rt}}',
            description: 'S-shaped curve with upper asymptote at K',
        },
    ],
    derivatives: (state, params) => {
        const K = getParam(params, 'K');
        const P0 = getParam(params, 'P0');
        const r = getParam(params, 'r');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        const P = K / (1 + ((K - P0) / P0) * Math.exp(-r * nextT));
        const dPdt = r * P * (1 - P / K);
        const inflection = Math.log((K - P0) / P0) / r;
        return {
            t: 0.016,
            P: P - getState(state, 'P'),
            dPdt: dPdt - getState(state, 'dPdt'),
            inflection: inflection - getState(state, 'inflection'),
        };
    },
    bounds: {
        t: { min: 0, max: 20 },
        P: { min: 0, max: 500 },
    },
});
// ============================================================================
// COMPOUND INTEREST ACCUMULATION
// ============================================================================
export const CompoundInterestAccumulation = createExperimentFromSchema({
    id: 'compound-interest-accumulation',
    name: 'Compound Interest Accumulation',
    domain: 'mathematics',
    description: 'Explore compound interest and continuous compounding',
    conceptTags: ['Compound Interest', 'e', 'Continuous Compounding', 'Financial Math'],
    difficulty: 3,
    stateVariables: [
        { id: 't', name: 'Time t', unit: 'years', initial: 0, symbol: 't' },
        { id: 'A', name: 'Amount A(t)', unit: 'dimensionless', initial: 1000, symbol: 'A(t)' },
        { id: 'interest', name: 'Total Interest', unit: 'dimensionless', initial: 0, symbol: 'I' },
        { id: 'continuous', name: 'Continuous A(t)', unit: 'dimensionless', initial: 1000, symbol: 'A_{cont}' },
    ],
    parameters: [
        { id: 'P', name: 'Principal P', unit: 'dimensionless', min: 100, max: 10000, default: 1000, symbol: 'P' },
        { id: 'r', name: 'Annual Rate', unit: 'dimensionless', min: 0.01, max: 0.3, default: 0.05, symbol: 'r' },
        { id: 'n', name: 'Compounding Frequency', unit: 'dimensionless', min: 1, max: 365, default: 12, symbol: 'n' },
    ],
    equations: [
        {
            id: 'compound-interest',
            name: 'Compound Interest Formula',
            latex: 'A(t) = P\\left(1 + \\frac{r}{n}\\right)^{nt}',
            description: 'Future value with compound interest',
        },
        {
            id: 'continuous-compounding',
            name: 'Continuous Compounding',
            latex: 'A(t) = Pe^{rt}',
            description: 'Limit as n approaches infinity',
        },
        {
            id: 'rule-of-72',
            name: 'Rule of 72',
            latex: 't_{double} \\approx \\frac{72}{r \\times 100}',
            description: 'Approximate time to double investment',
        },
    ],
    derivatives: (state, params) => {
        const P = getParam(params, 'P');
        const r = getParam(params, 'r');
        const n = Math.round(getParam(params, 'n'));
        const t = getState(state, 't');
        const nextT = t + 0.016;
        const A = P * Math.pow(1 + r / n, n * nextT);
        const interest = A - P;
        const continuous = P * Math.exp(r * nextT);
        return {
            t: 0.016,
            A: A - getState(state, 'A'),
            interest: interest - getState(state, 'interest'),
            continuous: continuous - getState(state, 'continuous'),
        };
    },
    bounds: {
        t: { min: 0, max: 50 },
        A: { min: 100, max: 50000 },
    },
});
// ============================================================================
// OPTIMIZATION PROBLEM SOLVING
// ============================================================================
export const OptimizationProblemSolving = createExperimentFromSchema({
    id: 'optimization-problem-solving',
    name: 'Optimization Problem Solving',
    domain: 'mathematics',
    description: 'Solve optimization problems using calculus',
    conceptTags: ['Optimization', 'Maxima/Minima', 'Constraints', 'Applications'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Decision Variable x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'objective', name: 'Objective f(x)', unit: 'dimensionless', initial: 1, symbol: 'f(x)' },
        { id: 'optimalX', name: 'Optimal x*', unit: 'dimensionless', initial: 2, symbol: 'x^*' },
        { id: 'optimalVal', name: 'Optimal Value', unit: 'dimensionless', initial: 4, symbol: 'f(x^*)' },
        { id: 'derivative', name: 'f\'(x)', unit: 'dimensionless', initial: -2, symbol: 'f\'(x)' },
    ],
    parameters: [
        { id: 'problemType', name: 'Problem Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'P' },
        { id: 'param', name: 'Parameter', unit: 'dimensionless', min: 1, max: 10, default: 4, symbol: 'p' },
    ],
    equations: [
        {
            id: 'first-derivative-test',
            name: 'First Derivative Test',
            latex: 'f\'(x) = 0 \\text{ and } f\'\'(x) < 0 \\implies \\text{local maximum}',
            description: 'Critical points where derivative equals zero',
        },
        {
            id: 'constraint',
            name: 'Constrained Optimization',
            latex: '\\max f(x) \\text{ s.t. } g(x) \\leq c',
            description: 'Optimize subject to constraints',
        },
    ],
    derivatives: (state, params) => {
        const problemType = Math.round(getParam(params, 'problemType'));
        const param = getParam(params, 'param');
        const x = getState(state, 'x');
        const nextX = x + 0.02;
        let objective = 0;
        let optimalX = 0;
        let optimalVal = 0;
        let derivative = 0;
        switch (problemType) {
            case 0: // Maximize area of rectangle with fixed perimeter
                // x is width, (p-2x)/2 is length, area = x(p-2x)/2
                optimalX = param / 4;
                optimalVal = optimalX * (param - 2 * optimalX) / 2;
                objective = nextX * (param - 2 * nextX) / 2;
                derivative = param / 2 - 2 * nextX;
                break;
            case 1: // Minimize surface area of box with fixed volume
                // For cube: x = V^(1/3)
                optimalX = Math.pow(param, 1 / 3);
                optimalVal = 6 * optimalX * optimalX;
                objective = 6 * nextX * nextX; // Simplified
                derivative = 12 * nextX;
                break;
            case 2: // Maximize x*(1-x) on [0,1]
                optimalX = 0.5;
                optimalVal = 0.25;
                objective = nextX * (1 - nextX);
                derivative = 1 - 2 * nextX;
                break;
            case 3: // Minimize x + 1/x for x > 0
                optimalX = 1;
                optimalVal = 2;
                objective = nextX + 1 / nextX;
                derivative = 1 - 1 / (nextX * nextX);
                break;
        }
        return {
            x: 0.02,
            objective: objective - getState(state, 'objective'),
            optimalX: optimalX - getState(state, 'optimalX'),
            optimalVal: optimalVal - getState(state, 'optimalVal'),
            derivative: derivative - getState(state, 'derivative'),
        };
    },
    bounds: {
        x: { min: 0.01, max: 10 },
        objective: { min: -10, max: 50 },
    },
});
// ============================================================================
// RATE OF CHANGE INTERPRETATION
// ============================================================================
export const RateOfChangeInterpretation = createExperimentFromSchema({
    id: 'rate-of-change-interpretation',
    name: 'Rate of Change Interpretation',
    domain: 'mathematics',
    description: 'Interpret rates of change in various contexts',
    conceptTags: ['Rate of Change', 'Derivatives', 'Related Rates', 'Applications'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time t', unit: 's', initial: 0, symbol: 't' },
        { id: 'x', name: 'Quantity x(t)', unit: 'dimensionless', initial: 0, symbol: 'x(t)' },
        { id: 'dxdt', name: 'Rate dx/dt', unit: 'dimensionless/s', initial: 2, symbol: '\\frac{dx}{dt}' },
        { id: 'd2xdt2', name: 'Acceleration d²x/dt²', unit: 'dimensionless/s²', initial: 0, symbol: '\\frac{d^2x}{dt^2}' },
    ],
    parameters: [
        { id: 'context', name: 'Context', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'C' },
        { id: 'velocity', name: 'Initial Velocity', unit: 'dimensionless/s', min: -10, max: 10, default: 5, symbol: 'v_0' },
        { id: 'accel', name: 'Acceleration', unit: 'dimensionless/s²', min: -5, max: 5, default: 0, symbol: 'a' },
    ],
    equations: [
        {
            id: 'rate-change',
            name: 'Rate of Change',
            latex: '\\frac{dx}{dt} = \\lim_{\\Delta t \\to 0} \\frac{x(t + \\Delta t) - x(t)}{\\Delta t}',
            description: 'Instantaneous rate of change',
        },
        {
            id: 'position-velocity-accel',
            name: 'Kinematic Relationships',
            latex: 'v(t) = \\frac{dx}{dt}, \\quad a(t) = \\frac{dv}{dt} = \\frac{d^2x}{dt^2}',
            description: 'Velocity and acceleration as derivatives',
        },
    ],
    derivatives: (state, params) => {
        const context = Math.round(getParam(params, 'context'));
        const v0 = getParam(params, 'velocity');
        const a = getParam(params, 'accel');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        let x = 0;
        let dxdt = 0;
        let d2xdt2 = 0;
        switch (context) {
            case 0: // Position with constant acceleration
                x = v0 * nextT + 0.5 * a * nextT * nextT;
                dxdt = v0 + a * nextT;
                d2xdt2 = a;
                break;
            case 1: // Exponential growth
                x = Math.exp(v0 * nextT);
                dxdt = v0 * Math.exp(v0 * nextT);
                d2xdt2 = v0 * v0 * Math.exp(v0 * nextT);
                break;
            case 2: // Sine wave
                x = Math.sin(v0 * nextT);
                dxdt = v0 * Math.cos(v0 * nextT);
                d2xdt2 = -v0 * v0 * Math.sin(v0 * nextT);
                break;
            case 3: // Damped oscillation
                const decay = Math.exp(-0.5 * nextT);
                x = decay * Math.sin(3 * nextT);
                dxdt = decay * (3 * Math.cos(3 * nextT) - 0.5 * Math.sin(3 * nextT));
                d2xdt2 = decay * ((-8.75) * Math.sin(3 * nextT) - 3 * Math.cos(3 * nextT));
                break;
        }
        return {
            t: 0.016,
            x: x - getState(state, 'x'),
            dxdt: dxdt - getState(state, 'dxdt'),
            d2xdt2: d2xdt2 - getState(state, 'd2xdt2'),
        };
    },
    bounds: {
        t: { min: 0, max: 30 },
        x: { min: -20, max: 50 },
    },
});
//# sourceMappingURL=modeling.js.map